import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Client, type IMessage, type StompSubscription } from "@stomp/stompjs";
import { baseApiUrl } from "../util/Constants";

const JWT_STORAGE_KEY = "jwt-auth-token";

const wsUrlFromApiUrl = (apiUrl: string): string => {
  const url = new URL(apiUrl);
  const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${url.host}${url.pathname.replace(/\/$/, "")}/ws`;
};

interface ChatSocketContextValue {
  isConnected: boolean;
  subscribe: (destination: string, callback: (payload: unknown) => void) => (() => void);
  publish: (destination: string, body: unknown) => void;
}

const ChatSocketContext = createContext<ChatSocketContextValue | null>(null);

interface ChatSocketProviderProps {
  children: ReactNode;
}

export function ChatSocketProvider({ children }: ChatSocketProviderProps) {
  const clientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const pendingSubs = useRef<Array<{ destination: string; callback: (msg: IMessage) => void; sub: StompSubscription | null }>>([]);

  useEffect(() => {
    const token = localStorage.getItem(JWT_STORAGE_KEY);
    const client = new Client({
      brokerURL: wsUrlFromApiUrl(baseApiUrl),
      connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      reconnectDelay: 3000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        setIsConnected(true);
        pendingSubs.current.forEach((entry) => {
          entry.sub = client.subscribe(entry.destination, entry.callback);
        });
      },
      onDisconnect: () => setIsConnected(false),
      onWebSocketClose: () => setIsConnected(false),
      onWebSocketError: (evt) => {
        // eslint-disable-next-line no-console
        console.error("[STOMP] websocket error", evt);
      },
      onStompError: (frame) => {
        // eslint-disable-next-line no-console
        console.error("[STOMP] error frame", frame.headers, frame.body);
        setIsConnected(false);
      },
    });
    clientRef.current = client;
    client.activate();

    return () => {
      pendingSubs.current.forEach((entry) => entry.sub?.unsubscribe());
      pendingSubs.current = [];
      client.deactivate().catch((): void => undefined);
      clientRef.current = null;
    };
  }, []);

  const value = useMemo<ChatSocketContextValue>(() => ({
    isConnected,
    subscribe: (destination, callback) => {
      const wrapped = (msg: IMessage) => {
        try {
          callback(JSON.parse(msg.body));
        } catch {
          callback(msg.body);
        }
      };
      const entry: { destination: string; callback: (msg: IMessage) => void; sub: StompSubscription | null } =
        { destination, callback: wrapped, sub: null };
      pendingSubs.current.push(entry);
      const client = clientRef.current;
      if (client?.connected) {
        entry.sub = client.subscribe(destination, wrapped);
      }
      return () => {
        entry.sub?.unsubscribe();
        pendingSubs.current = pendingSubs.current.filter((e) => e !== entry);
      };
    },
    publish: (destination, body) => {
      const client = clientRef.current;
      if (!client?.connected) return;
      client.publish({
        destination,
        body: JSON.stringify(body ?? {}),
        headers: { "content-type": "application/json" },
      });
    },
  }), [isConnected]);

  return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
}

export function useChatSocket(): ChatSocketContextValue {
  const ctx = useContext(ChatSocketContext);
  if (!ctx) {
    throw new Error("useChatSocket must be used within a ChatSocketProvider");
  }
  return ctx;
}
